import {
  Icon,
  Spinner,
  IconButton,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Box,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverHeader,
  PopoverBody,
  useDisclosure,
  HStack,
  Button,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import LowPriorityIcon from "@mui/icons-material/LowPriority";
import { useEffect, useState, useRef, useMemo } from "react";
import {
  PartsSearch,
  type PartSearchHit,
} from "@svan-garaaz/reactsharedlib/parts-search";
import { publicAxios } from "../../../config/axios";
import globalConstants from "../../../utils/constants";
import DeleteIcon from "@mui/icons-material/Delete";
import GaraazGeneralPopover from "./GaraazGeneralPopover";
import services from "./../services";
import { debounce as lodashDebounce } from "lodash";
import useNotification from "../../../utils/useNotification";
import { useOrdersStore } from "../store/useOrdersStore";
import GaraazPartConversionModal from "./GaraazPartConversionModal";
import FacetedPartSearch from "../../../components/FacetedPartSearch";
import { RiArrowRightUpLine } from "react-icons/ri";
import {
  FaSyncAlt,
  FaPause,
  FaPlay,
  FaTimes,
  FaPen,
  FaMagic,
} from "react-icons/fa";
import { usePartAvailability } from "../hooks/usePartAvailability";
import { usePartAvailabilityStore } from "../store/usePartAvailabilityStore";
import PartActionModal from "./PartActionModal";
import AddPartQuantityModal from "./AddPartQuantityModal";
import { OrderStatus, Roles, BillingEntity } from "../enums";
import { useUserDetailsContext } from "../../../context/userDetailsContext";
import { useGetOrderMediaConfig } from "../../PlaceOrderTest/queryHooks";
import { finalOrcaStates } from "./GaraazOrderComponent";
import { isAxiosError } from "axios";
import { isPartNumberCompatibleWithSearch } from "../../../utils/textUtils";
import { sortOrderItemsByDecisionPriority } from "../orderUtils";
import { pushCboItemsToAstuteBuddy } from "../utils/astuteBuddyCboPush";
import CboDetailsPopoverContent from "./CboDetailsPopoverContent";
import type { QuantityItem } from "../types/quantityItems";

type Props = {
  orderId: string;
  findBrandName: (brandId: string) => any;
  isDisabled: boolean;
  isEnquiryLocked?: boolean;
  orderData: any;
  componentHeight?: string;
  customerGeoPoint?: { latitude: number; longitude: number };
  refetchAllOrders?: () => void;
  /** When true (Enquiry Placed orders), search results show query highlights and a mismatch hint in the add-part modal. */
  enableEnquiryPartSearchUx?: boolean;
};

const GaraazPartConversion = (props: Props) => {
  const toast = useNotification();
  const userDetails = useUserDetailsContext();
  const { data: mobileConfigs } = useGetOrderMediaConfig();
  const {
    orderId,
    findBrandName,
    isDisabled,
    isEnquiryLocked = false,
    orderData,
    customerGeoPoint,
    refetchAllOrders,
    enableEnquiryPartSearchUx = false,
  } = props;
  const [lastPartSearchQuery, setLastPartSearchQuery] = useState("");
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isFacetedPartSearchOpen,
    onOpen: onFacetedPartSearchOpen,
    onClose: onFacetedPartSearchClose,
  } = useDisclosure();
  const {
    isOpen: isCancelHoldModalOpen,
    onOpen: onCancelHoldModalOpen,
    onClose: onCancelHoldModalClose,
  } = useDisclosure();
  const {
    isOpen: isQuantityModalOpen,
    onOpen: onQuantityModalOpen,
    onClose: onQuantityModalClose,
  } = useDisclosure();
  const [actionMode, setActionMode] = useState<
    "cancel" | "hold" | "unhold" | "add" | "cbo" | null
  >(null);
  const [selectedPartSnapshot, setSelectedPartSnapshot] = useState<any | null>(
    null,
  );
  const [quantity, setQuantity] = useState<string>("1");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEnquiryOrder =
    orderData?.requireEstimate === true &&
    orderData?.rootOrderId === orderData?._id;
  const isSupplierReadyOrder = useMemo(() => {
    return orderData?.allocatedTo?.some(
      (item: any) => item.isActive && item.orderStatus === OrderStatus.READY,
    );
  }, [orderData?.allocatedTo]);

  const partSearchHttp = useMemo(
    () => ({
      get: <T,>(
        url: string,
        config?: {
          params?: Record<string, string | number | boolean | undefined>;
        },
      ) =>
        publicAxios.get<T>(url, {
          ...config,
          headers: {
            Authorization:
              localStorage.getItem(globalConstants.AUTH_TOKEN) ?? "",
          },
        }),
    }),
    [],
  );

  const partSearchMismatch = useMemo(() => {
    if (!enableEnquiryPartSearchUx) return null;
    const part = selectedPartSnapshot;
    if (!part) return null;
    const q = lastPartSearchQuery.trim();
    if (!q) return null;
    const pn = String(part.partNumber ?? "").trim();
    const name = String(part.partName ?? "").trim();
    if (isPartNumberCompatibleWithSearch(lastPartSearchQuery, pn, name))
      return null;
    return {
      search: q,
      selectedPartNumber: pn,
      selectedPartName: name,
    };
  }, [enableEnquiryPartSearchUx, selectedPartSnapshot, lastPartSearchQuery]);
  const orderItems = useOrdersStore((state) => state.orderItems[orderId]);
  const setOrderItemsWithOrderId = useOrdersStore(
    (state) => state.setOrderItemsWithOrderId,
  );

  const [isPartEditDisabled, setIsPartEditDisabled] = useState(false);

  const displayOrderItems = useMemo(() => {
    const oi = Array.isArray(orderItems) ? orderItems : [];
    const sci = Array.isArray(orderData?.supplierCancelledItems)
      ? orderData.supplierCancelledItems
      : [];
    const cbo = Array.isArray(orderData?.cboItems) ? orderData.cboItems : [];
    const merged = new Map<string, any>();

    oi.forEach((item: any) => {
      const key = String(item?.partId ?? item?._id ?? "");
      if (!key) return;
      merged.set(key, {
        ...item,
        partId: item?.partId ?? item?._id,
        orderedQuantity: Number(item?.orderedQuantity || 0),
        remainingDecisionQuantity: Number(item?.remainingDecisionQuantity || 0),
        cancelledQuantity: Number(item?.cancelledQuantity || 0),
        holdQuantity: Number(item?.holdQuantity || 0),
        givenQuantity: Number(item?.givenQuantity || 0),
        approvedQuantity: Number(item?.approvedQuantity || 0),
        rejectedQuantity: Number(item?.rejectedQuantity || 0),
        supplierCancelledQuantity: Number(
          !isEnquiryOrder && isSupplierReadyOrder
            ? (item?.orderedQuantity ?? 0) - (item?.givenQuantity ?? 0) || 0
            : 0,
        ),
        supplierCancelledHoldQuantity: Number(
          item?.supplierCancelledHoldQuantity || 0,
        ),
        cboQuantity: Number(item?.cboQuantity || 0),
        cboHoldQuantity: Number(item?.cboHoldQuantity || 0),
      });
    });

    sci.forEach((item: any) => {
      const key = String(item?.partId ?? item?._id ?? "");
      if (!key) return;
      const existing = merged.get(key);
      if (!existing) {
        merged.set(key, {
          ...item,
          partId: item?.partId ?? item?._id,
          orderedQuantity: 0,
          remainingDecisionQuantity: 0,
          cancelledQuantity: Number(item?.cancelledQuantity || 0),
          holdQuantity: 0,
          givenQuantity: 0,
          approvedQuantity: 0,
          rejectedQuantity: 0,
          supplierCancelledQuantity: Number(
            item?.remainingDecisionQuantity ?? item?.orderedQuantity ?? 0,
          ),
          supplierCancelledHoldQuantity: Number(item?.holdQuantity || 0),
        });
        return;
      }

      merged.set(key, {
        ...existing,
        cancelledQuantity:
          Number(existing?.cancelledQuantity || 0) +
          Number(item?.cancelledQuantity || 0),
        supplierCancelledQuantity:
          Number(existing?.supplierCancelledQuantity || 0) +
          Number(item?.remainingDecisionQuantity || 0),
        supplierCancelledHoldQuantity:
          Number(existing?.supplierCancelledHoldQuantity || 0) +
          Number(item?.holdQuantity || 0),
      });
    });

    cbo.forEach((item: any) => {
      const key = String(item?.partId ?? item?._id ?? "");
      if (!key) return;
      const existing = merged.get(key);
      if (!existing) {
        merged.set(key, {
          ...item,
          partId: item?.partId ?? item?._id,
          orderedQuantity: 0,
          remainingDecisionQuantity: 0,
          cancelledQuantity: Number(item?.cancelledQuantity || 0),
          holdQuantity: 0,
          givenQuantity: 0,
          approvedQuantity: 0,
          rejectedQuantity: 0,
          supplierCancelledQuantity: 0,
          supplierCancelledHoldQuantity: 0,
          cboQuantity: Number(
            item?.remainingDecisionQuantity || item?.orderedQuantity || 0,
          ),
          cboHoldQuantity: Number(item?.holdQuantity || 0),
        });
        return;
      }

      merged.set(key, {
        ...existing,
        cancelledQuantity:
          Number(existing?.cancelledQuantity || 0) +
          Number(item?.cancelledQuantity || 0),
        cboQuantity:
          Number(existing?.cboQuantity || 0) +
          Number(item?.remainingDecisionQuantity || 0),
        cboHoldQuantity:
          Number(existing?.cboHoldQuantity || 0) +
          Number(item?.holdQuantity || 0),
      });
    });

    return Array.from(merged.values());
  }, [orderItems, orderData?.supplierCancelledItems, orderData?.cboItems]);

  const supplierCancelledItems = useMemo<QuantityItem[]>(
    () =>
      Array.isArray(orderData?.supplierCancelledItems)
        ? orderData.supplierCancelledItems
        : [],
    [orderData?.supplierCancelledItems],
  );
  const cboItems = useMemo<QuantityItem[]>(
    () => (Array.isArray(orderData?.cboItems) ? orderData.cboItems : []),
    [orderData?.cboItems],
  );
  const hasActiveCancellable = useMemo(
    () =>
      Array.isArray(orderItems) &&
      orderItems.some(
        (item: any) => Number(item?.remainingDecisionQuantity || 0) > 0,
      ),
    [orderItems],
  );
  const hasSupplierCancelledCancellable = useMemo(
    () =>
      supplierCancelledItems.some(
        (item: QuantityItem) => Number(item?.remainingDecisionQuantity || 0) > 0,
      ),
    [supplierCancelledItems],
  );
  const hasActiveHoldable = useMemo(
    () =>
      Array.isArray(orderItems) &&
      orderItems.some(
        (item: any) => Number(item?.remainingDecisionQuantity || 0) > 0,
      ),
    [orderItems],
  );
  const hasSupplierCancelledHoldable = useMemo(
    () =>
      supplierCancelledItems.some(
        (item: QuantityItem) => Number(item?.remainingDecisionQuantity || 0) > 0,
      ),
    [supplierCancelledItems],
  );
  const hasActiveUnholdable = useMemo(
    () =>
      Array.isArray(orderItems) &&
      orderItems.some((item: any) => Number(item?.holdQuantity || 0) > 0),
    [orderItems],
  );
  const hasSupplierCancelledUnholdable = useMemo(
    () =>
      supplierCancelledItems.some(
        (item: QuantityItem) => Number(item?.holdQuantity || 0) > 0,
      ),
    [supplierCancelledItems],
  );
  const hasCboCancellable = useMemo(
    () =>
      cboItems.some(
        (item: QuantityItem) =>
          Number(item?.remainingDecisionQuantity || 0) > 0,
      ),
    [cboItems],
  );
  const hasCboHoldable = useMemo(
    () =>
      cboItems.some(
        (item: QuantityItem) =>
          Number(item?.remainingDecisionQuantity || 0) > 0,
      ),
    [cboItems],
  );
  const hasCboUnholdable = useMemo(
    () => cboItems.some((item: QuantityItem) => Number(item?.holdQuantity || 0) > 0),
    [cboItems],
  );

  const hasAnyCustomerCancelled = useMemo(
    () =>
      displayOrderItems.some(
        (item: any) => Number(item?.cancelledQuantity || 0) > 0,
      ),
    [displayOrderItems],
  );
  const hasAnyHold = useMemo(
    () =>
      displayOrderItems.some(
        (item: any) => Number(item?.holdQuantity || 0) > 0,
      ),
    [displayOrderItems],
  );
  const hasAnySupplierCancelledHold = useMemo(
    () =>
      displayOrderItems.some(
        (item: any) => Number(item?.supplierCancelledHoldQuantity || 0) > 0,
      ),
    [displayOrderItems],
  );
  const hasAnySupplierCancelled = useMemo(
    () =>
      displayOrderItems.some(
        (item: any) => Number(item?.supplierCancelledQuantity || 0) > 0,
      ),
    [displayOrderItems],
  );
  const hasAnyCbo = useMemo(
    () =>
      displayOrderItems.some((item: any) => Number(item?.cboQuantity || 0) > 0),
    [displayOrderItems],
  );
  const hasAnyCboHold = useMemo(
    () =>
      displayOrderItems.some(
        (item: any) => Number(item?.cboHoldQuantity || 0) > 0,
      ),
    [displayOrderItems],
  );

  useEffect(() => {
    const orderStatus = orderData.orderStatus;
    const isRootOrder = orderData._id === orderData.rootOrderId;
    setIsPartEditDisabled(
      isEnquiryLocked ||
        !(orderData?.requireEstimate && isRootOrder) ||
        orderData.orderType === "RETURN" ||
        orderData.orderType === "SUBSCRIPTION" ||
        finalOrcaStates.includes(orderStatus) ||
        orderStatus === OrderStatus.READY_TO_DISPATCH ||
        orderStatus === OrderStatus.DELIVERED,
    );
  }, [userDetails?.userSubType, orderData, mobileConfigs, isEnquiryLocked]);

  const isInsuranceEnquiryOrder = useMemo(() => {
    const isInsuranceEntity =
      orderData?.billingDetails?.billingEntity ===
      BillingEntity.INSURANCE_COMPANY;
    const insurerFamily =
      orderData?.billingAddress?.billingCustomerInsurerFamily ??
      orderData?.billingDetails?.billingCustomerInsurerFamily;
    const eligibleFamilies =
      mobileConfigs?.eligible_insurer_families_for_approval ?? [];
    const isEligibleForApproval =
      Array.isArray(eligibleFamilies) &&
      eligibleFamilies.length > 0 &&
      typeof insurerFamily === "string" &&
      eligibleFamilies.some(
        (f: string) =>
          String(f).trim().toLowerCase() ===
          String(insurerFamily).trim().toLowerCase(),
      );
    return isEnquiryOrder && isInsuranceEntity && isEligibleForApproval;
  }, [
    orderData?.requireEstimate,
    orderData?.rootOrderId,
    orderData?._id,
    orderData?.billingDetails?.billingEntity,
    orderData?.billingAddress?.billingCustomerInsurerFamily,
    orderData?.billingDetails?.billingCustomerInsurerFamily,
    mobileConfigs?.eligible_insurer_families_for_approval,
  ]);

  // Cleanup debounced functions on unmount
  useEffect(() => {
    return () => {
      debouncedUpdatePartRef.current.forEach((debouncedFunc) => {
        if (debouncedFunc && typeof debouncedFunc.cancel === "function") {
          debouncedFunc.cancel();
        }
      });
      debouncedUpdatePartRef.current.clear();
    };
  }, []);

  const [isRefetching, setIsRefetching] = useState(false);
  const debouncedUpdatePartRef = useRef<
    Map<string, ReturnType<typeof lodashDebounce>>
  >(new Map());
  const {
    isAvailabilityColumnVisible,
    setAvailabilityColumnVisible,
    getAvailabilityData,
  } = usePartAvailabilityStore();

  const showAvailabilityColumn = isAvailabilityColumnVisible(orderId);

  const {
    availabilityMap,
    availabilityLoading,
    fetchAvailability,
    lastFetchTime,
  } = usePartAvailability({
    orderId,
    orderItems,
    customerGeoPoint,
    enabled: false,
  });

  const openAvailabilityPage = () => {
    try {
      const partsForAvailabilityCheck = orderItems.map((item: any) => ({
        rootPartNumber: item.rootPartNumber,
        partNumber: item.partNumber,
        quantity: item.remainingDecisionQuantity,
        partName: item.partName,
      }));

      const searchCriteria = {
        parts: partsForAvailabilityCheck,
        customerGeoPoint: customerGeoPoint,
        orderNumber: orderData?.orderNo,
      };
      const url = `/part-availability?criteria=${encodeURIComponent(
        JSON.stringify(searchCriteria),
      )}`;
      window.open(url, "_blank");
    } catch (error) {
      console.error("Error opening availability page:", error);
    }
  };

  useEffect(() => {
    const hasData =
      getAvailabilityData(orderId) && getAvailabilityData(orderId)!.length > 0;
    if (hasData && !showAvailabilityColumn) {
      setAvailabilityColumnVisible(orderId, true);
    }
  }, [
    orderId,
    showAvailabilityColumn,
    getAvailabilityData,
    setAvailabilityColumnVisible,
  ]);

  useEffect(() => {
    if (availabilityMap.size > 0 && isRefetching) {
      setIsRefetching(false);
    }
  }, [availabilityMap, isRefetching]);

  function handlePartSelect(hit: PartSearchHit, query: string) {
    if (isPartEditDisabled) return;

    const alreadyExists = orderItems.some(
      (part: any) => part.partId === hit._id,
    );
    if (alreadyExists) {
      toast({
        description: "Part already exists in order",
        status: "error",
      });
      return;
    }

    setLastPartSearchQuery(query);
    setSelectedPartSnapshot(hit);
    setQuantity("1");
    onQuantityModalOpen();
  }

  async function handleAddPartWithQuantity() {
    if (isPartEditDisabled) return;

    const part = selectedPartSnapshot;
    if (!part) return;

    const quantityNum = parseInt(quantity);

    // Validate quantity
    if (isNaN(quantityNum) || quantityNum < 1) {
      toast({
        description: "Quantity must be at least 1",
        status: "error",
      });
      return;
    }
    const alreadyExists = orderItems.some(
      (item: any) => item.partId === part._id,
    );

    if (alreadyExists) {
      toast({
        description: "Part already exists in order",
        status: "error",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      await services.orderItemsAction(orderData.orderNo, "ADD", [
        {
          partId: part._id,
          quantity: quantityNum,
        },
      ]);
      setOrderItemsWithOrderId(orderId, [
        {
          ...part,
          partId: part._id,
          orderedQuantity: quantityNum,
          remainingDecisionQuantity: quantityNum,
          holdQuantity: 0,
          cancelledQuantity: 0,
          givenQuantity: 0,
        },
        ...orderItems,
      ]);
      setLastPartSearchQuery("");
      onQuantityModalClose();
      setSelectedPartSnapshot(null);
      setQuantity("1");
      refetchAllOrders?.();
    } catch (error) {
      let errorMessage =
        "There was an error in Inserting the part, please try again";
      if (isAxiosError(error)) {
        errorMessage =
          error.response?.data?.statusCode === 409
            ? error.response?.data?.errorMsg
            : errorMessage;
      }
      toast({
        description: errorMessage,
        status: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  }
  async function handleAddPartFromPartSearch_2_0(
    part: any,
    quantityNum: number,
  ) {
    if (isPartEditDisabled) return;

    await services.orderItemsAction(orderData.orderNo, "ADD", [
      {
        partId: part._id || part.partId,
        quantity: quantityNum,
      },
    ]);
    refetchAllOrders?.();
  }

  async function handleDeleteRow(part: any) {
    if (isDisabled) return;

    try {
      const updatedPart = await services.deletePart(orderId, {
        partId: part.partId,
        orderedQuantity: part.orderedQuantity,
      });

      let updatedOrderItems = [...orderItems];
      const partIndex = updatedOrderItems.findIndex(
        (item) =>
          item.partId === part.partId ||
          (item.partNumber === part.partNumber &&
            item.rootPartNumber === part.rootPartNumber),
      );

      if (partIndex !== -1) {
        updatedOrderItems.splice(partIndex, 1);
        setOrderItemsWithOrderId(orderId, updatedOrderItems);
      }
    } catch (error: any) {
      toast({
        description:
          "There was an error in deleting the part, please try again",
        status: "error",
      });
    }
  }
  function remove_idFromPartDetails(partObject: any) {
    return {
      "Part Name": partObject?.partName,
      "Part Number": partObject.partNumber,
      MRP: partObject.mrp,
      Brand: partObject.brandId
        ? findBrandName(partObject.brandId)
        : partObject.brand,
      "HSN code": partObject.hsnCode,
      "Root Part Number": partObject.rootPartNumber,
    };
  }
  function handleQuanityValueChange(
    e: React.ChangeEvent<HTMLInputElement>,
    part: any,
  ) {
    if (isDisabled) return;

    try {
      let updatedOrderItems = [...orderItems];
      const partIndex = updatedOrderItems.findIndex(
        (item) =>
          item.partId === part.partId ||
          (item.partNumber === part.partNumber &&
            item.rootPartNumber === part.rootPartNumber),
      );

      if (partIndex !== -1) {
        updatedOrderItems[partIndex].orderedQuantity = parseInt(e.target.value);
        if (parseInt(e.target.value) > 0) {
          updatedOrderItems[partIndex].remainingDecisionQuantity = parseInt(
            e.target.value,
          );
        }
        setOrderItemsWithOrderId(orderId, updatedOrderItems);

        // Get or create debounced function for this part
        if (!debouncedUpdatePartRef.current.has(part.partId)) {
          debouncedUpdatePartRef.current.set(
            part.partId,
            lodashDebounce(async (partId: string, quantity: number) => {
              const partData = {
                partId: partId,
                orderedQuantity: quantity,
                remainingDecisionQuantity: quantity,
              };
              if (partData.orderedQuantity > 0) {
                await services.insertOrUpdatePart(orderId, partData);
              }
            }, 2000),
          );
        }

        const debouncedFunc = debouncedUpdatePartRef.current.get(part.partId);
        if (e.target.value !== "" && e.target.value !== "0" && debouncedFunc) {
          debouncedFunc(part.partId, parseInt(e.target.value));
        }
      }
    } catch (error) {
      toast({
        description:
          "There was an error in updating the part, please try again ",
        status: "error",
      });
    }
  }

  const handleRefetchAvailabilityClick = () => {
    setIsRefetching(true);
    setAvailabilityColumnVisible(orderId, true);
    fetchAvailability().finally(() => {
      setIsRefetching(false);
    });
  };

  const handleAutoFillCboToAstuteBuddy = () => {
    pushCboItemsToAstuteBuddy(
      orderData?.orderNo || "",
      cboItems,
      orderData?.customer?.name || "",
    );
    toast({
      description: "CBO parts have been filled in Astute Buddy",
      status: "success",
    });
  };

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      flex={1}
      width={"100%"}
      maxHeight={props.componentHeight || "100%"}
      style={{ margin: 0 }}
    >
      <Box
        display={"flex"}
        alignItems={"center"}
        width={"100%"}
        gap={2}
        position="relative"
        zIndex={1000}
      >
        <Box flex={1} minW={0}>
          <PartsSearch
            axios={partSearchHttp}
            brand={orderData?.carMaker}
            onSelect={handlePartSelect}
            disabled={isPartEditDisabled}
            disabledHint=""
          />
        </Box>
        <Icon
          fontSize={"2xl"}
          flexShrink={0}
          onClick={isPartEditDisabled ? undefined : onFacetedPartSearchOpen}
          cursor={isPartEditDisabled ? "not-allowed" : "pointer"}
          opacity={isPartEditDisabled ? 0.6 : 1}
        >
          <RiArrowRightUpLine fontWeight={"bold"}></RiArrowRightUpLine>
        </Icon>
      </Box>

      <Box
        mt={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pl={1}
        bg="gray.50"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
        position="relative"
      >
        {(availabilityLoading || isRefetching) && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="rgba(255, 255, 255, 0.8)"
            borderRadius="md"
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={1}
          >
            <HStack spacing={2}>
              <Spinner size="sm" color="blue.500" />
              <Text fontSize="x-small" color="blue.600" fontWeight="500">
                Fetching availability data...
              </Text>
            </HStack>
          </Box>
        )}
        <HStack spacing={0}>
          <Text fontSize="x-small" fontWeight="500" color="gray.700">
            📊 Fetch Availability
          </Text>
          <IconButton
            size="sm"
            color="blue.500"
            icon={
              availabilityLoading || isRefetching ? (
                <Spinner size="xs" />
              ) : (
                <Icon as={FaSyncAlt} />
              )
            }
            bg="transparent"
            aria-label="Refetch part availability"
            onClick={handleRefetchAvailabilityClick}
            isDisabled={
              availabilityLoading || isRefetching || orderItems.length === 0
            }
            transition="all 0.2s"
            _hover={{
              bg: "transparent",
              transform: "scale(1.1)",
            }}
          />
          {showAvailabilityColumn && (
            <HStack spacing={2}>
              {lastFetchTime && (
                <Text fontSize="x-small" color="gray.600" fontWeight="500">
                  Last updated: {lastFetchTime.toLocaleString()}
                </Text>
              )}
            </HStack>
          )}
        </HStack>
        <HStack spacing={2}>
          {orderData.requireEstimate && (
            <HStack spacing={3} alignItems="center">
              <Tooltip label="Auto fill CBO to Astute Buddy">
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="blue"
                  leftIcon={<FaMagic />}
                  fontSize="2xs"
                  fontWeight="600"
                  bg="white"
                  borderColor="blue.200"
                  color="blue.600"
                  _hover={{ bg: "blue.50" }}
                  onClick={handleAutoFillCboToAstuteBuddy}
                  isDisabled={
                    isPartEditDisabled ||
                    !cboItems.some(
                      (item: QuantityItem) =>
                        Number(item?.remainingDecisionQuantity ?? 0) > 0 &&
                        String(item?.partNumber ?? "").trim().length > 0,
                    )
                  }
                >
                  Auto fill CBO to Astute Buddy
                </Button>
              </Tooltip>
              <Box h="24px" w="1px" bg="gray.200" flexShrink={0} aria-hidden />
              <HStack spacing={2}>
                <Tooltip label="Add Quantity">
                  <IconButton
                    icon={<FaPen />}
                    backgroundColor={"#3182CE"}
                    color={"white"}
                    aria-label={""}
                    onClick={() => {
                      setActionMode("add");
                      onCancelHoldModalOpen();
                    }}
                    padding={0.5}
                    aspectRatio={"1/1"}
                    height={"100%"}
                    size="xs"
                    fontSize="xs"
                    isDisabled={orderItems.length === 0 || isPartEditDisabled}
                    _hover={{
                      backgroundColor: "#3182CE",
                    }}
                  />
                </Tooltip>
                <Tooltip label="Cancel Items">
                  <IconButton
                    icon={<FaTimes />}
                    backgroundColor={"#FF0000"}
                    color={"white"}
                    aria-label={""}
                    onClick={() => {
                      setActionMode("cancel");
                      onCancelHoldModalOpen();
                    }}
                    padding={0.5}
                    aspectRatio={"1/1"}
                    height={"100%"}
                    size="xs"
                    fontSize="xs"
                    isDisabled={
                      (!hasActiveCancellable &&
                        !hasSupplierCancelledCancellable &&
                        !hasCboCancellable) ||
                      isPartEditDisabled
                    }
                    _hover={{
                      backgroundColor: "#FF0000",
                    }}
                  />
                </Tooltip>

                <Tooltip label="Hold Items">
                  <IconButton
                    icon={<FaPause />}
                    backgroundColor={"#FFA500"}
                    color={"white"}
                    aria-label={""}
                    onClick={() => {
                      setActionMode("hold");
                      onCancelHoldModalOpen();
                    }}
                    padding={0.5}
                    aspectRatio={"1/1"}
                    height={"100%"}
                    size="xs"
                    fontSize="2xs"
                    isDisabled={
                      (!hasActiveHoldable &&
                        !hasSupplierCancelledHoldable &&
                        !hasCboHoldable) ||
                      isPartEditDisabled
                    }
                    _hover={{
                      backgroundColor: "#FFA500",
                    }}
                  />
                </Tooltip>
                <Tooltip label="Re-Activate Items">
                  <IconButton
                    icon={<FaPlay />}
                    backgroundColor={" #02b002"}
                    color={"white"}
                    aria-label={""}
                    onClick={() => {
                      setActionMode("unhold");
                      onCancelHoldModalOpen();
                    }}
                    padding={0.5}
                    aspectRatio={"1/1"}
                    height={"100%"}
                    size="xs"
                    fontSize="2xs"
                    isDisabled={
                      (!hasActiveUnholdable &&
                        !hasSupplierCancelledUnholdable &&
                        !hasCboUnholdable) ||
                      isPartEditDisabled
                    }
                    _hover={{
                      backgroundColor: "#02b002",
                    }}
                  />
                </Tooltip>
                <Tooltip label="Move to CBO">
                  <IconButton
                    icon={<LowPriorityIcon fontSize="small" />}
                    backgroundColor={"#C05621"}
                    color={"white"}
                    aria-label={""}
                    onClick={() => {
                      setActionMode("cbo");
                      onCancelHoldModalOpen();
                    }}
                    padding={0.5}
                    aspectRatio={"1/1"}
                    height={"100%"}
                    size="xs"
                    fontSize="2xs"
                    isDisabled={
                      (!hasActiveCancellable &&
                        !hasSupplierCancelledCancellable) ||
                      isPartEditDisabled
                    }
                    _hover={{
                      backgroundColor: "#C05621",
                    }}
                  />
                </Tooltip>
              </HStack>
            </HStack>
          )}
          <Button
            size="xs"
            fontSize="2xs"
            variant="solid"
            colorScheme="blue"
            onClick={openAvailabilityPage}
            borderRadius="md"
            disabled={orderItems.length === 0}
          >
            Detailed Analysis
          </Button>
        </HStack>
      </Box>

      <Box
        width={"100%"}
        height={"75%"}
        mt={1}
        overflowY={"scroll"}
        boxSizing="border-box"
      >
        <Table
          variant="simple"
          width={"100%"}
          overflowY={"scroll"}
          height={"100%"}
        >
          <Thead width={"100%"} bg="white" position="sticky" top={0} zIndex={1}>
            <Tr
              padding={"5px"}
              display={"flex"}
              width={"100%"}
              justifyContent={"space-between"}
              alignItems={"center"}
              borderBottom={"1px solid #EDF2F6"}
            >
              <Th border={"none"} padding={"0"} width={"20%"}>
                <Text fontSize={"x-small"}>Part No.</Text>
              </Th>

              <Th
                border={"none"}
                padding={"0"}
                isNumeric
                width={orderData?.orderType === "RETURN" ? "25%" : "15%"}
                textAlign={"center"}
              >
                <Text textAlign={"center"} fontSize={"x-small"}>
                  {orderData?.orderType === "RETURN"
                    ? "Returned Ordered"
                    : "Ordered"}
                </Text>
              </Th>
              {orderData.orderType === "REGULAR" &&
                !orderData.requireEstimate && (
                  <Th
                    border={"none"}
                    padding={"0"}
                    isNumeric
                    width={"15%"}
                    textAlign={"center"}
                  >
                    <Text textAlign={"center"} fontSize={"x-small"}>
                      Given
                    </Text>
                  </Th>
                )}
              {orderData.requireEstimate && (
                <>
                  <Th
                    border={"none"}
                    padding={"0"}
                    isNumeric
                    width={"15%"}
                    textAlign={"center"}
                  >
                    <Text textAlign={"center"} fontSize={"x-small"}>
                      Active
                    </Text>
                  </Th>
                  {hasAnyCustomerCancelled && (
                    <Th
                      border={"none"}
                      padding={"0"}
                      isNumeric
                      width={"15%"}
                      textAlign={"center"}
                    >
                      <Tooltip label="Customer Cancelled" hasArrow>
                        <Text
                          textAlign={"center"}
                          fontSize={"0.70rem"}
                          cursor="pointer"
                        >
                          CC
                        </Text>
                      </Tooltip>
                    </Th>
                  )}
                  {hasAnyHold && (
                    <Th
                      border={"none"}
                      padding={"0"}
                      isNumeric
                      width={"15%"}
                      textAlign={"center"}
                    >
                      <Text textAlign={"center"} fontSize={"x-small"}>
                        Hold
                      </Text>
                    </Th>
                  )}
                  {isInsuranceEnquiryOrder && (
                    <>
                      <Th
                        border={"none"}
                        padding={"0"}
                        isNumeric
                        width={"15%"}
                        textAlign={"center"}
                      >
                        <Text textAlign={"center"} fontSize={"x-small"}>
                          Approved
                        </Text>
                      </Th>
                      <Th
                        border={"none"}
                        padding={"0"}
                        isNumeric
                        width={"15%"}
                        textAlign={"center"}
                      >
                        <Text textAlign={"center"} fontSize={"x-small"}>
                          Rejected
                        </Text>
                      </Th>
                    </>
                  )}
                </>
              )}
              {hasAnySupplierCancelled && (
                <Th
                  border={"none"}
                  padding={"0"}
                  isNumeric
                  width={"15%"}
                  textAlign={"center"}
                >
                  <Tooltip label="Supplier Cancelled" hasArrow>
                    <Text
                      textAlign={"center"}
                      fontSize={"0.70rem"}
                      cursor="pointer"
                    >
                      SC
                    </Text>
                  </Tooltip>
                </Th>
              )}
              {hasAnySupplierCancelledHold && (
                <Th
                  border={"none"}
                  padding={"0"}
                  isNumeric
                  width={"15%"}
                  textAlign={"center"}
                >
                  <Tooltip label="Hold After Supplier Cancellation" hasArrow>
                    <Text
                      textAlign={"center"}
                      fontSize={"0.70rem"}
                      cursor="pointer"
                    >
                      HSC
                    </Text>
                  </Tooltip>
                </Th>
              )}
              {hasAnyCbo && (
                <Th border={"none"} padding={"0"} width={"8%"} isNumeric>
                  <Tooltip label="CBO" hasArrow>
                    <Text
                      textAlign={"center"}
                      fontSize={"x-small"}
                      cursor="pointer"
                    >
                      CBO
                    </Text>
                  </Tooltip>
                </Th>
              )}
              {hasAnyCboHold && (
                <Th border={"none"} padding={"0"} width={"8%"} isNumeric>
                  <Tooltip label="Hold After CBO" hasArrow>
                    <Text
                      textAlign={"center"}
                      fontSize={"x-small"}
                      cursor="pointer"
                    >
                      HCBO
                    </Text>
                  </Tooltip>
                </Th>
              )}
              {showAvailabilityColumn && (
                <Th
                  border={"none"}
                  padding={"0"}
                  width={"25%"}
                  textAlign={"center"}
                >
                  <Text textAlign={"center"} fontSize={"x-small"}>
                    Availability
                  </Text>
                </Th>
              )}
              <Th
                border={"none"}
                padding={"0"}
                width={"15%"}
                textAlign={"end"}
                cursor={"pointer"}
                mr={"1em"}
              >
                <Text
                  fontSize={"x-small"}
                  color={"#0096e0"}
                  onClick={() => {
                    onOpen();
                  }}
                  textAlign={"center"}
                >
                  {displayOrderItems.length} Parts Added
                </Text>
              </Th>
            </Tr>
          </Thead>
          <Tbody width={"100%"} height={"100%"}>
            {displayOrderItems.length > 0 &&
              Object.entries(displayOrderItems[0])?.length !== 0 &&
              (() => {
                let sortedOrderItems = sortOrderItemsByDecisionPriority([
                  ...displayOrderItems,
                ]);

                if (showAvailabilityColumn) {
                  sortedOrderItems?.sort((a, b) => {
                    const availabilityA = availabilityMap.get(a.rootPartNumber);
                    const availabilityB = availabilityMap.get(b.rootPartNumber);
                    if (availabilityA && availabilityB) {
                      const percentageDiff =
                        availabilityB.quantityFulfilledPercentage -
                        availabilityA.quantityFulfilledPercentage;

                      if (percentageDiff === 0) {
                        return (
                          availabilityB.quantityFulfilledCount -
                          availabilityA.quantityFulfilledCount
                        );
                      }

                      return percentageDiff;
                    }

                    if (availabilityA && !availabilityB) return -1;
                    if (!availabilityA && availabilityB) return 1;

                    return 0;
                  });
                }

                return sortedOrderItems?.map((part: any, index: number) => {
                  const availability = availabilityMap.get(part.rootPartNumber);

                  return (
                    <Tr
                      key={`${part.partNumber}-${index}`}
                      padding={"2px"}
                      fontSize={"x-small"}
                      width={"100%"}
                      textColor={"black"}
                      display={"flex"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      onMouseEnter={() => {
                        setHoveredIndex(index);
                      }}
                      onMouseLeave={() => {
                        setHoveredIndex(null);
                      }}
                      _hover={{
                        backgroundColor: "#F0F6F6",
                      }}
                      transition="all 0.2s"
                    >
                      <Td padding={"0"} width={"20%"} border={"none"}>
                        <GaraazGeneralPopover
                          ArekeysVisible={true}
                          CopierKeys={[]}
                          title="Part Details"
                          value={[remove_idFromPartDetails(part)]}
                        >
                          <PopoverTrigger>
                            <Text
                              cursor={"pointer"}
                              noOfLines={1}
                              whiteSpace={"normal"}
                              fontSize={"x-small"}
                              color={"#1a202c"}
                            >
                              {part.partNumber}
                            </Text>
                          </PopoverTrigger>
                        </GaraazGeneralPopover>
                      </Td>

                      {/* <Td padding={"0"} width={"15%"} isNumeric border={"none"}>
                        <Text width={"100%"} textAlign={"center"}>
                          <Input
                            isDisabled={isDisabled}
                            id={`part-quantity-${orderId}-${index}`}
                            backgroundColor={"white"}
                            size={"xs"}
                            type="number"
                            padding={0}
                            width={"50%"}
                            maxWidth={"50px"}
                            zIndex={0}
                            paddingLeft={1}
                            value={part.orderedQuantity}
                            min={1}
                            onChange={(e) => {
                              handleQuanityValueChange(e, part);
                            }}
                            height={"85%"}
                          ></Input>
                        </Text>
                      </Td> */}
                      <Td padding={"0"} width={"15%"} isNumeric border={"none"}>
                        <Text width={"100%"} textAlign={"center"}>
                          {part.orderedQuantity}
                        </Text>
                      </Td>
                      {orderData.orderType === "REGULAR" &&
                        !orderData.requireEstimate && (
                          <Td
                            padding={"0"}
                            width={"15%"}
                            isNumeric
                            border={"none"}
                          >
                            <Text width={"100%"} textAlign={"center"}>
                              {part.givenQuantity || 0}
                            </Text>
                          </Td>
                        )}
                      {orderData.requireEstimate && (
                        <>
                          <Td
                            padding={"0"}
                            width={"15%"}
                            isNumeric
                            border={"none"}
                          >
                            <Text width={"100%"} textAlign={"center"}>
                              {part.remainingDecisionQuantity}
                            </Text>
                          </Td>
                          {hasAnyCustomerCancelled && (
                            <Td
                              padding={"0"}
                              width={"15%"}
                              isNumeric
                              border={"none"}
                            >
                              <Text width={"100%"} textAlign={"center"}>
                                {part.cancelledQuantity || 0}
                              </Text>
                            </Td>
                          )}
                          {hasAnyHold && (
                            <Td
                              padding={"0"}
                              width={"15%"}
                              isNumeric
                              border={"none"}
                            >
                              <Text width={"100%"} textAlign={"center"}>
                                {part.holdQuantity || 0}
                              </Text>
                            </Td>
                          )}
                          {isInsuranceEnquiryOrder && (
                            <>
                              <Td
                                padding={"0"}
                                width={"15%"}
                                isNumeric
                                border={"none"}
                              >
                                <Text width={"100%"} textAlign={"center"}>
                                  {part.approvedQuantity || 0}
                                </Text>
                              </Td>
                              <Td
                                padding={"0"}
                                width={"15%"}
                                isNumeric
                                border={"none"}
                              >
                                <Text width={"100%"} textAlign={"center"}>
                                  {part.rejectedQuantity || 0}
                                </Text>
                              </Td>
                            </>
                          )}
                        </>
                      )}
                      {hasAnySupplierCancelled && (
                        <Td
                          padding={"0"}
                          width={"15%"}
                          isNumeric
                          border={"none"}
                        >
                          <Text width={"100%"} textAlign={"center"}>
                            {part.supplierCancelledQuantity || 0}
                          </Text>
                        </Td>
                      )}
                      {hasAnySupplierCancelledHold && (
                        <Td
                          padding={"0"}
                          width={"15%"}
                          isNumeric
                          border={"none"}
                        >
                          <Text width={"100%"} textAlign={"center"}>
                            {part.supplierCancelledHoldQuantity || 0}
                          </Text>
                        </Td>
                      )}
                      {hasAnyCbo && (
                        <Td
                          border={"none"}
                          padding={"0"}
                          width={"8%"}
                          textAlign={"center"}
                          isNumeric
                        >
                          <Text width={"100%"} textAlign={"center"}>
                            {part.cboQuantity || 0}
                          </Text>
                        </Td>
                      )}
                      {hasAnyCboHold && (
                        <Td
                          border={"none"}
                          padding={"0"}
                          width={"8%"}
                          textAlign={"center"}
                          isNumeric
                        >
                          <Text width={"100%"} textAlign={"center"}>
                            {part.cboHoldQuantity || 0}
                          </Text>
                        </Td>
                      )}
                      {showAvailabilityColumn && (
                        <Td
                          padding={"0"}
                          width={"25%"}
                          border={"none"}
                          textAlign={"center"}
                        >
                          {availabilityLoading ? (
                            <Spinner size="xs" />
                          ) : availability ? (
                            <Text
                              fontSize="xs"
                              fontWeight="900"
                              color={
                                availability.quantityFulfilledPercentage >= 80
                                  ? "green.500"
                                  : availability.quantityFulfilledPercentage >=
                                      60
                                    ? "yellow.600"
                                    : availability.quantityFulfilledPercentage >=
                                        40
                                      ? "yellow.400"
                                      : availability.quantityFulfilledPercentage >=
                                          20
                                        ? "orange.500"
                                        : "red.500"
                              }
                            >
                              {availability.quantityFulfilledCount}(
                              {availability.quantityFulfilledPercentage}%)
                            </Text>
                          ) : (
                            <Text fontSize="xs" color="gray.500">
                              Not Fetched
                            </Text>
                          )}
                        </Td>
                      )}
                      <Td
                        width={"15%"}
                        textAlign={"end"}
                        padding={"0"}
                        border={"none"}
                        opacity={`${hoveredIndex === index ? 1 : 0}`}
                        marginRight={"1.5em"}
                      >
                        <Popover
                          trigger="hover"
                          placement="right-start"
                          gutter={4}
                          isLazy
                          lazyBehavior="keepMounted"
                        >
                          <PopoverTrigger>
                            <Box display="inline-flex" alignItems="center">
                              <InfoOutlineIcon
                                color="blue.500"
                                cursor="pointer"
                                boxSize={4}
                              />
                            </Box>
                          </PopoverTrigger>
                          <PopoverContent
                            p={2}
                            minW="300px"
                            w="fit-content"
                            maxW="calc(100vw - 32px)"
                            textAlign="left"
                          >
                            <PopoverArrow />
                            <PopoverHeader fontWeight="700" fontSize="lg" color="gray.800">
                              {`CBO Details - ${part.partNumber}`}
                            </PopoverHeader>
                            <PopoverBody p={0} overflowX="auto">
                              <CboDetailsPopoverContent
                                rootPartNumber={String(
                                  part.rootPartNumber || part.partNumber || "",
                                )}
                                partNumber={String(part.partNumber || "")}
                              />
                            </PopoverBody>
                          </PopoverContent>
                        </Popover>
                        {/* <IconButton
                          isDisabled={isDisabled}
                          aria-label=""
                          onClick={() => {
                            handleDeleteRow(part);
                          }}
                          backgroundColor={"#BBE6E3"}
                          size={"xs"}
                          height={"20px"}
                        >
                          <DeleteIcon
                            style={{ fontSize: "1.3em" }}
                          ></DeleteIcon>
                        </IconButton> */}
                      </Td>
                    </Tr>
                  );
                });
              })()}
          </Tbody>
        </Table>
      </Box>
      <FacetedPartSearch
        isOpen={isFacetedPartSearchOpen}
        onClose={onFacetedPartSearchClose}
        orderItems={orderItems}
        onAddPart={handleAddPartFromPartSearch_2_0}
        remove_idFromPartDetails={remove_idFromPartDetails}
        isAddDisabled={isPartEditDisabled}
      ></FacetedPartSearch>
      <GaraazPartConversionModal
        refetchAllOrders={refetchAllOrders}
        isDisabled={isDisabled}
        isOpen={isOpen}
        onClose={onClose}
        orderId={orderId}
        remove_idFromPartDetails={remove_idFromPartDetails}
        orderData={orderData}
        isPartEditDisabled={isPartEditDisabled}
        handleQuanityValueChange={handleQuanityValueChange}
        enableEnquiryPartSearchUx={enableEnquiryPartSearchUx}
      ></GaraazPartConversionModal>
      <PartActionModal
        isOpen={isCancelHoldModalOpen}
        onClose={() => {
          onCancelHoldModalClose();
          setActionMode(null);
        }}
        isDisabled={isPartEditDisabled}
        orderId={orderId}
        orderNo={orderData?.orderNo || ""}
        customerName={orderData?.customer?.name || ""}
        mode={actionMode}
        supplierCancelledItems={supplierCancelledItems}
        cboItems={cboItems}
        refetchAllOrders={refetchAllOrders}
      />
      <AddPartQuantityModal
        isOpen={isQuantityModalOpen}
        onClose={() => {
          onQuantityModalClose();
          setSelectedPartSnapshot(null);
          setQuantity("1");
          setLastPartSearchQuery("");
        }}
        selectedPart={selectedPartSnapshot}
        quantity={quantity}
        onQuantityChange={setQuantity}
        onConfirm={handleAddPartWithQuantity}
        remove_idFromPartDetails={remove_idFromPartDetails}
        isLoading={isSubmitting}
        partSearchMismatch={partSearchMismatch}
      />
    </Box>
  );
};

export default GaraazPartConversion;
